// Stage ⓪ — panel duyệt bảng bản địa hoá sang phương Tây (Epic 8, đợt 260903).
//
// 🔴 Đứng NGAY DƯỚI cổng duyệt khung xương, chỉ hiện khi outline APPROVED — localize chạy sau cổng.
// Bảng khung xương phía trên vẫn hiện TÊN GỐC (để sửa cast không nhầm); tên phương Tây xem ở đây.
import { friendlyApiError } from './workbench/jobErrorText';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Card, Collapse, Input, Modal, Popconfirm, Radio, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import Button from './workbench/WbButton';
import { adaptationApi } from '../../services/api/adaptation';
import { adaptationAssistantApi } from '../../services/api/adaptationAssistant';
import type {
  FilmGlossaryTerm, FilmGlossaryType, LocalizeMode, LocalizeOp, LocalizePatchResponse, LocalizeVersionFull, LocalizeVersionMeta, LocalizeView, WorldSettingLite,
} from '../../types/adaptation';
import LocalizeEditModal, { applyLocalizeOpsDirect, renderSections } from './workbench/LocalizeEditModal';
import ConsequenceModal from './workbench/ConsequenceModal';
import { glossaryDeleteCopy } from './workbench/consequenceCopy';
import { applyResultView, editErrorText, ApplyResultView } from './workbench/localizeEditView';
import { versionTitle } from './episodeFixUtils';
import { useFilmJobPoll } from './useFilmJobPoll';
import AdaptationGateCard from './AdaptationGateCard';
import { useAdoptedJobId } from './workbench/useAdoptedJobId';
import {
  GLOSSARY_TYPE_LABELS, groupLocalizeIssues, groupLocalizeOthers, summarizeAliasScan, summarizeLocalize, buildPolicyBody, describeAdaptationError, gateLabel,
} from './adaptationUtils';
import { localizeNameCounts, localizeStatusLine } from './workbench/entityTableView';
import {
  acceptAllCopy, DEFAULT_TERM_FILTER, gateNeedsAttention, glossaryStatusLabel, glossaryTermsView, localizeIssueAction, pinnedLine, policyDraftState, policyLockedCopy, POLICY_CARDS, policyModeLabel, REVERT_WORLD_CONFIRM_TITLE,
  termFilterOptions, TermFilterKey, warningToastLevel, worldAutoCardView, worldBlockMode, worldRows,
} from './workbench/localizeStageView';
import { gateSpentUsd, gateView } from './gateView';
import { useCtaHolds } from './workbench/WorkbenchContext';
import OptionsButton from './workbench/assistant/OptionsButton';
import OtherIssuesBlock from './workbench/OtherIssuesBlock';
import { localizeChipsOf } from './workbench/assistant/assistantView';
import { localizeOptionsRequest } from './workbench/assistant/localizeIssueResolve';
import type { AssistantRequest } from './workbench/assistant/assistantIntent';
import { plainJobMessage } from './workbench/workbenchView';
import type { LocalizeIssueChip } from '../../types/adaptationAssistant';

const { Text } = Typography;

interface Props {
  storyId: string;
  onChanged: () => void;
  /** E1-P3a — bảng tên sống ở ② (Nhân vật/Bối cảnh); ③ chỉ còn thẻ tóm tắt + đường sang ②. */
  onOpenEntities: (t: { kind?: 'character' | 'location'; filter?: 'missing' }) => void;
  /** E3-P3a — mốc `film_outlines.modified_at` màn hình đang giữ (khôi phục phiên bản ③ cần CAS). */
  outlineModifiedAt?: string | null;
  /** 261005 E3-P3b (Lát 4) — mở khung Trợ lý ③ với intent (nút "Phương án (tốn AI)" tự gửi 3 phương án). Thiếu ⇒ không có nút. */
  onAskAssistant?: (req: AssistantRequest) => void;
  /** Lý do tắt các nút "Phương án" (chặng đang khoá…); không có ⇒ bấm được. */
  askDisabledReason?: string;
  /** F36 — có job nền đang chạy theo BE (`workbench.jobs.running`, kể cả job do phiên trước / chặng khác bắt đầu): khoá mọi nút ghi như `working`. */
  externalBusy?: boolean;
  /** F36 — id job bản địa hoá BE báo đang chạy (không gồm chấm lại cổng) để theo dõi tiếp sau tải lại / đổi chặng. */
  adoptJobId?: string | null;
  /** F36 — id job chấm lại cổng AI (phase review-gate) BE báo đang chạy, chuyển cho thẻ Cổng AI. */
  adoptGateJobId?: string | null;
  /** 261015 P5a (D6) — truyện đã có dòng kịch bản nào chưa (⑤): bật/tắt "Hoàn tác" của thẻ bối cảnh AI tự ghi (BE chặn 409 `HAS_WRITTEN_EPISODES`). */
  hasWrittenEpisodes?: boolean;
}

const GLOSSARY_TYPE_OPTIONS = (Object.keys(GLOSSARY_TYPE_LABELS) as FilmGlossaryType[])
  .map((v) => ({ value: v, label: GLOSSARY_TYPE_LABELS[v] }));

const worldLine = (ws: LocalizeView['targetWorldSetting']): string =>
  ws ? [ws.era, ws.world, ws.architecture, ws.costume].filter(Boolean).join(' · ') : '';

const AdaptationLocalize: React.FC<Props> = ({ storyId, onChanged, onOpenEntities, outlineModifiedAt, onAskAssistant, askDisabledReason, externalBusy, adoptJobId, adoptGateJobId, hasWrittenEpisodes = false }) => {
  const stageCta = useCtaHolds().holds('stage');   // 3h3: đỏ chỉ khi không có dải giữ việc
  const [view, setView] = useState<LocalizeView | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [delTerm, setDelTerm] = useState<FilmGlossaryTerm | null>(null);   // 3h4c: từ vựng đang mở «hộp hậu quả» xoá
  const { jobId: bgJobId, setJobId: setBgJobId, release: releaseBgJob } = useAdoptedJobId(storyId, adoptJobId ?? null);
  /** F36 — khoá theo CẢ state cục bộ lẫn job BE đang chạy (sau tải lại state cục bộ mất). */
  const working = !!busy || !!bgJobId || !!externalBusy;
  const busyReason = working ? 'Đang có tác vụ chạy nền — chờ xong rồi thử lại' : undefined;
  const optionsReason = askDisabledReason ?? busyReason;
  /** E3-P1 (Y2): bản nháp chính sách đang chọn — chỉ ghi khi bấm "Lưu chính sách". */
  const [draftMode, setDraftMode] = useState<LocalizeMode>('WESTERNIZE');
  const [draftCulture, setDraftCulture] = useState('');
  /** Bản nháp ô "Trong phim" của từ vựng (id → chuỗi) — lưu khi blur/Enter. */
  const [termDraft, setTermDraft] = useState<Record<string, string>>({});
  const termInflight = useRef(false);
  /** 3h4b1: nhóm từ vựng đang xem — mặc định «Cần bạn» (chỉ có tác dụng khi BE gửi `termDecisions`). */
  const [termFilter, setTermFilter] = useState<TermFilterKey>(DEFAULT_TERM_FILTER);
  const [newTerm, setNewTerm] = useState<{ o: string; l: string; t: FilmGlossaryType }>({ o: '', l: '', t: 'OTHER' });
  /** E3-P3a: lệnh ③ đang chờ xem thử (đổi từ vựng / bối cảnh) · phiên bản ③ · hộp sửa bối cảnh · kết quả khôi phục (giữ tới khi đóng). */
  const [pending, setPending] = useState<{ ops: LocalizeOp[]; title: string } | null>(null);
  const [versions, setVersions] = useState<LocalizeVersionMeta[] | null>(null);
  const [viewVer, setViewVer] = useState<LocalizeVersionFull | null>(null);
  const [worldEdit, setWorldEdit] = useState<WorldSettingLite | null>(null);
  const [restoreResult, setRestoreResult] = useState<ApplyResultView | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await adaptationApi.getLocalize(storyId));
    } catch {
      setView(null);
    }
    try { setVersions(await adaptationApi.localizeVersions(storyId)); } catch { setVersions(null); }
  }, [storyId]);

  // Áp/khôi phục từ Trợ lý làm outline đổi `modifiedAt`; nạp lại ③ để alias/glossary/cảnh báo là bản hiện hành.
  useEffect(() => { load(); }, [load, outlineModifiedAt]);
  // 261005 E3-P3b (Lát 4) — chip do BE cấp (`GET scope localize`, 0 AI) để nút "Phương án" tick đúng vấn đề. Lỗi / chưa tải ⇒ null ⇒ nút gửi bằng chữ (không tick).
  const [assistChips, setAssistChips] = useState<LocalizeIssueChip[] | null>(null);
  useEffect(() => {
    if (!onAskAssistant || !view || view.localizeEnabled === false) { setAssistChips(null); return undefined; }
    let alive = true;
    adaptationAssistantApi.scope({ storyId, stageKey: 'localize', scopeKey: '' })
      .then((v) => { if (alive) setAssistChips(localizeChipsOf(v)); })
      .catch(() => { if (alive) setAssistChips(null); });
    return () => { alive = false; };
  }, [storyId, view, outlineModifiedAt, !!onAskAssistant]); // eslint-disable-line react-hooks/exhaustive-deps
  const askOptions = (ref: Parameters<typeof localizeOptionsRequest>[1]) => { if (onAskAssistant) onAskAssistant(localizeOptionsRequest(assistChips, ref)); };
  const optionsBtn = (ref: Parameters<typeof localizeOptionsRequest>[1]) => (onAskAssistant
    ? <OptionsButton disabledReason={optionsReason} onClick={() => askOptions(ref)} />
    : null);
  useEffect(() => { setTermDraft({}); }, [storyId]);
  useEffect(() => {
    // Đồng bộ bản nháp với chính sách đã ghim mỗi khi view đổi (sau lưu / tải lại).
    setDraftMode(view?.policy?.mode ?? 'WESTERNIZE');
    setDraftCulture(view?.policy?.targetCulture ?? '');
  }, [view?.policy?.mode, view?.policy?.targetCulture]);

  useFilmJobPoll(bgJobId, {
    onTerminal: async (outcome, task) => {
      releaseBgJob();
      if (outcome === 'completed') message.success('Bản địa hoá xong.');
      else if (outcome === 'failed') {
        // LOCALIZE_INCOMPLETE… không phải lỗi HTTP: chỉ lộ qua `detail.error` của job, dạng "MÃ: mô tả".
        const text: string = task?.detail?.error || '';
        const code = /^([A-Z][A-Z_]{5,}):/.exec(text)?.[1];
        const d = code ? describeAdaptationError({ response: { status: 409, data: { code, message: text } } }) : null;
        message.error(d ? `${d.title} — ${d.lines[0]}` : 'Job bản địa hoá thất bại — xem log.', 10);
      }
      else message.warning('Đã dừng theo dõi — bấm Tải lại.');
      await load();
      onChanged();
    },
  });

  const runBg = async (key: string, fn: () => Promise<{ jobId?: string; message: string }>) => {
    setBusy(key);
    try {
      const r = await fn();
      if (!r.jobId) { message.warning('Server không trả job id.'); return; }
      setBgJobId(r.jobId);
      message.info(plainJobMessage(r.message), 5);
    } catch (e: any) {
      message.error(friendlyApiError(e, 'Không chạy được'), 8);
    } finally {
      setBusy(null);
    }
  };

  const mutate = async (key: string, fn: () => Promise<any>, okMsg?: string) => {
    setBusy(key);
    try {
      await fn();
      if (okMsg) message.success(okMsg);
      await load();
      onChanged();
    } catch (e: any) {
      message.error(friendlyApiError(e, 'Không lưu được'), 6);
    } finally {
      setBusy(null);
    }
  };

  /** E3-P1 (Y2): ghim chính sách. 409 LOCALIZE_POLICY_LOCKED ⇒ hỏi xác nhận rồi gửi lại `force:true`. */
  const savePolicy = async (force?: boolean) => {
    const built = buildPolicyBody(draftMode, draftCulture, force);
    if (built.ok === false) { message.warning(built.reason); return; }
    setBusy('policy');
    try {
      const r = await adaptationApi.putPolicy(storyId, built.body);
      message.success(r.changed ? 'Đã ghim chính sách' : 'Chính sách không đổi', 5);
      (r.warnings ?? []).forEach((w) => message[warningToastLevel(w.kind)](w.message, 10));
      await load();
      onChanged();
    } catch (e) {
      const d = describeAdaptationError(e);
      if (d.forceable && !force) {
        const c = policyLockedCopy({ fromMode: view?.policy?.mode ?? 'WESTERNIZE', toMode: draftMode });
        Modal.confirm({
          title: c.title, content: c.content, okText: c.okText, cancelText: 'Thôi',
          okButtonProps: { danger: c.danger }, onOk: () => savePolicy(true),
        });
      } else {
        message.error(`${d.title}${d.lines[0] ? ` — ${d.lines[0]}` : ''}`, 8);
      }
    } finally {
      setBusy(null);
    }
  };

  /** E3-P1 (Y2): duyệt bối cảnh đề xuất. 409 WORLD_SETTING_PINNED ⇒ xác nhận rồi `force:true`. */
  const acceptWorld = async (force?: boolean) => {
    setBusy('accept-world');
    try {
      const r = await adaptationApi.acceptWorld(storyId, force ? { force: true } : {});
      message.success(r.eraChanged ? 'Đã duyệt bối cảnh (thời đại đổi)' : 'Đã duyệt bối cảnh', 5);
      (r.warnings ?? []).forEach((w) => message[warningToastLevel(w.kind)](w.message, 12));
      await load();
      onChanged();
    } catch (e) {
      const d = describeAdaptationError(e);
      if (d.forceable && !force) {
        Modal.confirm({
          title: d.title, content: d.lines.join(' '), okText: 'Vẫn duyệt', cancelText: 'Thôi',
          onOk: () => acceptWorld(true),
        });
      } else {
        message.error(`${d.title}${d.lines[0] ? ` — ${d.lines[0]}` : ''}`, 8);
      }
    } finally {
      setBusy(null);
    }
  };

  /** 261015 P5a (D6): Hoàn tác CHỈ bối cảnh AI vừa tự ghi (op `revertAutoWorld` qua đường PATCH ops có sẵn — CAS + phiên bản ③; không khôi phục phiên bản vì nó chụp bản SAU). */
  const revertAutoWorld = async () => {
    setBusy('revert-world');
    try {
      await applyLocalizeOpsDirect(storyId, [{ op: 'revertAutoWorld' }]);
      message.success('Đã bỏ bối cảnh AI vừa ghi — bối cảnh trở về chờ duyệt.', 6);
      await load();
      onChanged();
    } catch (e) {
      const t = editErrorText(e);
      message.error(t.text, 8);
      if (t.refresh) { await load(); onChanged(); }
    } finally {
      setBusy(null);
    }
  };

  const summary = useMemo(() => summarizeLocalize(view), [view]);
  const aliasScan = useMemo(() => summarizeAliasScan(view), [view]);
  const issues = useMemo(() => groupLocalizeIssues(view), [view]); // 261015 P1: CHỈ lỗi thật
  const otherIssues = useMemo(() => groupLocalizeOthers(view), [view]); // thông báo/điểm/… ⇒ khối gập, không nút trợ lý

  // E1-P3a — thẻ "Tên trong phim": tóm tắt + lối sang bảng ở ② (không hook: dùng được cả ở nhánh đã tắt).
  const names = localizeNameCounts(view);
  const namesCard = (
    <Card size="small" title="Tên trong phim" id="section-localize-names">
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {names.namedChars}/{names.totalChars} nhân vật có tên · {names.namedLocs}/{names.totalLocs} bối cảnh · {names.locked} đang giữ
        </Text>
        {names.missingChars.length > 0 && (
          <>
            <Text style={{ fontSize: 13 }}>
              Chưa có tên: {names.missingChars.slice(0, 10).map((c) => c.name).join(', ')}
              {names.missingChars.length > 10 ? ` (+${names.missingChars.length - 10})` : ''}
            </Text>
            <Space wrap>
              <Button size="small" type="primary" onClick={() => onOpenEntities({ kind: 'character', filter: 'missing' })}>
                Đặt tên ở bảng Nhân vật ②
              </Button>
              {optionsBtn({ kind: 'missingName', chars: names.missingChars.slice(0, 30) })}
              {!view?.resultStale && view?.localizeEnabled !== false && (
                <Text type="secondary" style={{ fontSize: 13 }}>hoặc bấm “Bồi chỗ trống” để AI đặt</Text>
              )}
            </Space>
          </>
        )}
        <Space wrap>
          <Button size="small" type="link" style={{ padding: 0 }} onClick={() => onOpenEntities({ kind: 'character' })}>Mở bảng Nhân vật (②)</Button>
          <Button size="small" type="link" style={{ padding: 0 }} onClick={() => onOpenEntities({ kind: 'location' })}>Mở bảng Bối cảnh (②)</Button>
        </Space>
      </Space>
    </Card>
  );

  if (view && view.localizeEnabled === false) {
    const line = localizeStatusLine({ localizeEnabled: false, hasRun: summary.hasRun });
    return (
      <Card size="small" title="Bản địa hoá" id="section-localize">
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Alert type="info" showIcon message="Bản địa hoá đang tắt cho truyện này" description={line?.text} />
          {summary.hasRun && namesCard}
        </Space>
      </Card>
    );
  }


  /** Đổi chữ "Trong phim" của term ⇒ có thể thay trên kịch bản đã viết ⇒ modal XEM THỬ (E3-P3a). */
  const saveTerm = (g: FilmGlossaryTerm) => {
    const next = (termDraft[g.id] ?? g.localizedTerm).trim();
    const clear = () => setTermDraft((d) => { const { [g.id]: _x, ...rest } = d; return rest; });
    if (!next || next === g.localizedTerm) { clear(); return; }
    if (pending) return; // Enter rồi Blur: chỉ một lần mở
    setPending({ ops: [{ op: 'glossaryUpsert', originalTerm: g.originalTerm, localizedTerm: next, type: g.type }], title: `Đổi từ vựng "${g.originalTerm}" → "${next}"` });
    clear();
  };
  const directOps = (key: string, ops: LocalizeOp[], okMsg?: string) => mutate(key, () => applyLocalizeOpsDirect(storyId, ops), okMsg);
  const openVersion = async (v: LocalizeVersionMeta) => {
    try { setViewVer(await adaptationApi.localizeVersion(storyId, v.id)); } catch (e) { message.error(editErrorText(e).text, 6); }
  };
  const restoreVersion = async (v: LocalizeVersionMeta) => {
    if (!outlineModifiedAt) { message.warning('Chưa có mốc dữ liệu — tải lại trang'); return; }
    setBusy(`rv-${v.id}`);
    try {
      const r: LocalizePatchResponse = await adaptationApi.restoreLocalizeVersion(storyId, v.id, outlineModifiedAt);
      setRestoreResult(applyResultView(r));
      await load();
      onChanged();
    } catch (e) {
      message.error(editErrorText(e).text, 8);
      if (editErrorText(e).refresh) onChanged();
    } finally { setBusy(null); }
  };

  const termsView = glossaryTermsView(view?.glossary, view?.termDecisions, termFilter);
  const termRowById = new Map(termsView.rows.map((r) => [r.term.id, r]));
  const TERM_TAG_COLOR = { NEEDS_REVIEW: 'orange', AUTO: 'blue', ACCEPTED: 'green' } as const;
  const glossaryColumns = [
    { title: 'Trong truyện', dataIndex: 'originalTerm', key: 'o', width: 200, render: (v: string) => <Text>{v}</Text> },
    {
      title: 'Trong phim', key: 'l',
      render: (_: unknown, g: FilmGlossaryTerm) => (
        <Input
          size="small" disabled={working} value={termDraft[g.id] ?? g.localizedTerm}
          onChange={(e) => setTermDraft((d) => ({ ...d, [g.id]: e.target.value }))}
          onBlur={() => saveTerm(g)} onPressEnter={() => saveTerm(g)}
        />
      ),
    },
    {
      title: 'Loại', key: 't', width: 140,
      render: (_: unknown, g: FilmGlossaryTerm) => (
        <Select
          size="small" value={g.type} style={{ width: 130 }} disabled={working}
          options={GLOSSARY_TYPE_OPTIONS}
          onChange={(type) => directOps(`gt-${g.id}`, [{ op: 'glossaryUpsert', originalTerm: g.originalTerm, localizedTerm: g.localizedTerm, type }])}
        />
      ),
    },
    {
      title: 'Trạng thái', key: 's', width: termsView.enabled ? 260 : 120,
      render: (_: unknown, g: FilmGlossaryTerm) => {
        const r = termRowById.get(g.id);
        if (!termsView.enabled || !r?.decision) {
          return <Tag color={g.status === 'APPROVED' ? 'green' : 'orange'}>{glossaryStatusLabel(g.status)}</Tag>;
        }
        return (
          <Space direction="vertical" size={2}>
            <Tag color={TERM_TAG_COLOR[r.decision]} style={{ marginInlineEnd: 0 }}>{r.label}</Tag>
            <Text type="secondary" style={{ fontSize: 13 }}>{r.reason}</Text>
          </Space>
        );
      },
    },
    {
      title: '', key: 'act', width: 150,
      render: (_: unknown, g: FilmGlossaryTerm) => (
        <Space size={0}>
          {g.status !== 'APPROVED' && (
            <Button size="small" type="link" disabled={working}
              onClick={() => mutate(`ga-${g.id}`, () => adaptationApi.updateGlossary(g.id, { status: 'APPROVED' }), 'Đã chấp nhận')}>
              Chấp nhận
            </Button>
          )}
          <Button size="small" type="link" danger disabled={working} onClick={() => setDelTerm(g)}>Xoá</Button>
        </Space>
      ),
    },
  ];

  // ── Dữ liệu cho các khối ──────────────────────────────────────────────────
  const hasRun = summary.hasRun;
  const pinned = view?.implicit ? undefined : view?.policy;
  const policyState = policyDraftState({ draftMode, draftCulture, pinned, implicit: view?.implicit });
  const gateInfo = gateView(view?.reviewGates?.localize, {
    stepKey: 'localize', hasResult: hasRun, spentUsd: view?.reviewGates ? gateSpentUsd(view.reviewGates) : null,
  });
  const gateOutside = gateNeedsAttention(gateInfo?.state);
  const world = worldRows(view?.targetWorldSetting);
  const proposedWorld = view?.worldSettingStatus === 'PROPOSED' && view.proposedWorldSetting ? worldRows(view.proposedWorldSetting) : null;
  const worldMode = worldBlockMode(view, { hasWrittenEpisodes });
  const worldAutoCard = worldMode === 'auto' ? worldAutoCardView(view, { hasWrittenEpisodes }) : null;
  const cultureLabel = policyModeLabel(view?.policy?.mode ?? 'WESTERNIZE');

  const gateCard = (
    <AdaptationGateCard
      storyId={storyId} stepKey="localize" gates={view?.reviewGates} hasResult={hasRun}
      onRerunStep={() => runBg('rerun', () => adaptationApi.runLocalize(storyId))}
      disabled={working} adoptJobId={adoptGateJobId}
      onChanged={() => { load(); onChanged(); }}
      onAskOptions={onAskAssistant ? (issue) => askOptions({ kind: 'gate', problem: issue.problem, where: issue.where }) : undefined}
      askOptionsDisabledReason={onAskAssistant ? optionsReason : undefined}
    />
  );

  const actionRow = (
    <Space wrap>
      {hasRun && (
        <>
          {/* Hai nút, vì "chạy lại" từng có đúng một nghĩa: sáng tác lại cả thế giới (vá 260907). */}
          <Popconfirm
            title="Bồi chỗ trống?" description="Giữ nguyên bối cảnh đích, tên đã có và term đã dịch — AI chỉ điền phần còn thiếu. Việc này tốn AI; chỉ chạy khi bạn xác nhận."
            okText="Chạy" cancelText="Thôi" disabled={working || !!view?.resultStale}
            onConfirm={() => runBg('rerun', () => adaptationApi.runLocalize(storyId))}
          >
            <Tooltip title="Giữ nguyên bối cảnh đích, tên đã có và term đã dịch — chỉ điền phần còn thiếu">
              <Button loading={!!bgJobId || busy === 'rerun'} disabled={working || !!view?.resultStale}>
                Bồi chỗ trống
              </Button>
            </Tooltip>
          </Popconfirm>
          <Popconfirm
            title="Làm lại bản địa hoá từ đầu?"
            description="Bối cảnh đích, mọi tên chưa khoá và term chưa duyệt sẽ được đặt lại — kết quả sẽ khác lần này. Việc này tốn AI."
            okText="Làm lại" cancelText="Thôi" disabled={working}
            onConfirm={() => runBg('redo', () => adaptationApi.runLocalize(storyId, { redo: true }))}
          >
            <Button danger loading={busy === 'redo'} disabled={working}>Làm lại từ đầu</Button>
          </Popconfirm>
        </>
      )}
      <Button onClick={load} disabled={working}>Tải lại</Button>
    </Space>
  );

  const policyBlock = (
    <Card size="small" title="Cách bản địa hoá">
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Radio.Group value={draftMode} disabled={working} onChange={(e) => setDraftMode(e.target.value)} style={{ width: '100%' }} aria-label="Cách bản địa hoá">
          <div className="wb-choices">
            {POLICY_CARDS.map((c) => (
              <Radio key={c.mode} value={c.mode} className="wb-choice">
                <Text strong>{c.title}</Text>
                <span className="wb-choice-hint">{c.hint}</span>
              </Radio>
            ))}
          </div>
        </Radio.Group>
        {draftMode === 'MAP' && (
          <Space direction="vertical" size={2}>
            <Text type="secondary" style={{ fontSize: 13 }}>Văn hoá đích (2–60 ký tự)</Text>
            <Input
              size="small" style={{ width: 240 }} placeholder="Ví dụ: Ả Rập, Nhật…" maxLength={60}
              value={draftCulture} onChange={(e) => setDraftCulture(e.target.value)} disabled={working}
            />
          </Space>
        )}
        <Space wrap align="center">
          <Text type="secondary" style={{ fontSize: 13 }}>
            {view?.policyError
              ? `Cách bản địa hoá trong khung xương không đọc được: ${view.policyError.message}`
              : pinnedLine({ policy: view?.policy, implicit: view?.implicit })}
          </Text>
          {policyState.dirty && (
            <Tooltip title={policyState.canSave ? undefined : policyState.reason}>
              <span>
                <Button size="small" type="primary" loading={busy === 'policy'} disabled={working || !policyState.canSave} onClick={() => savePolicy()}>
                  Lưu cách bản địa hoá
                </Button>
              </span>
            </Tooltip>
          )}
        </Space>
        {view?.resultStale && (
          <Alert
            type="warning" showIcon
            message="Kết quả bản địa hoá thuộc cách cũ"
            description="Chạy lại: bấm “Làm lại từ đầu”. “Bồi chỗ trống” đang bị khoá vì sẽ giữ tên của cách cũ."
          />
        )}
      </Space>
    </Card>
  );

  const worldList = (rows: { label: string; value: string }[]) => (
    <Space direction="vertical" size={2} style={{ width: '100%' }}>
      {rows.map((r) => (
        <Text key={r.label} style={{ fontSize: 13 }}><Text strong>{r.label}: </Text>{r.value}</Text>
      ))}
    </Space>
  );

  const worldBlock = (
    <Card
      size="small" title="Bối cảnh phim"
      extra={!world.empty && !worldAutoCard && (
        <Button size="small" disabled={working} onClick={() => setWorldEdit({ ...(view?.targetWorldSetting ?? {}) })}>Sửa</Button>
      )}
    >
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        {proposedWorld && (
          <Card
            size="small" type="inner" style={{ borderColor: 'var(--wb-warn)' }} title="Đề xuất — chờ duyệt"
            extra={(
              <Popconfirm
                title="Duyệt bối cảnh này?"
                description="Sẽ ghi vào bối cảnh đích của truyện — mọi ảnh/kịch bản sau đó bám theo."
                okText="Duyệt" cancelText="Thôi" onConfirm={() => acceptWorld()}
              >
                <Button size="small" type="primary" loading={busy === 'accept-world'} disabled={working}>Duyệt</Button>
              </Popconfirm>
            )}
          >
            {worldList(proposedWorld.rows)}
            {view?.proposalStale && <Text type="danger" style={{ fontSize: 13 }}>Đề xuất thuộc cách cũ — chạy lại bản địa hoá để có đề xuất mới.</Text>}
          </Card>
        )}
        {worldAutoCard && (
          <Card
            size="small" type="inner" style={{ borderColor: 'var(--wb-ok, #52c41a)' }} title={worldAutoCard.title} data-testid="world-auto-card"
            extra={(
              <Space size={4}>
                <Tooltip title={worldAutoCard.revertDisabledReason ?? undefined}>
                  <span>
                    <Popconfirm title={REVERT_WORLD_CONFIRM_TITLE} okText="Hoàn tác" cancelText="Thôi" disabled={working || !worldAutoCard.canRevert} onConfirm={revertAutoWorld}>
                      <Button size="small" loading={busy === 'revert-world'} disabled={working || !worldAutoCard.canRevert}>Hoàn tác</Button>
                    </Popconfirm>
                  </span>
                </Tooltip>
                <Button size="small" disabled={working} onClick={() => setWorldEdit({ ...(view?.targetWorldSetting ?? {}) })}>Sửa</Button>
              </Space>
            )}
          >
            <Text type="secondary" style={{ fontSize: 13 }}>AI ghi thẳng vào bối cảnh phim theo cách “{cultureLabel}” vì bối cảnh trước đó rỗng hoặc do AI ghi, chưa ghim, chưa có tập nào viết.</Text>
          </Card>
        )}
        {world.empty
          ? <Text type="secondary" style={{ fontSize: 13 }}>Chưa có bối cảnh phim — sẽ có sau khi chạy bản địa hoá{proposedWorld ? ' và duyệt đề xuất' : ''}.</Text>
          : worldList(world.rows)}
      </Space>
    </Card>
  );

  const needsBlock = hasRun ? (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      {/* E9-P3: vi phạm của lượt gần nhất — trước đây chỉ nằm trong log (TD-ADAPT-17). Luôn hiện, không gập. */}
      {issues.length > 0 && (
        <Alert
          type={issues.some((i) => i.severity === 'ERROR') ? 'error' : 'warning'}
          showIcon
          message={`Lượt bản địa hoá gần nhất có ${issues.reduce((n, i) => n + i.count, 0)} cảnh báo`}
          description={(
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              {issues.map((i) => (
                <Text key={i.gate} style={{ fontSize: 13 }}>
                  <Tag color={i.severity === 'ERROR' ? 'red' : 'orange'}>{gateLabel(i.gate)}</Tag>
                  {i.count > 1 ? `×${i.count} · ` : ''}{i.message}{' '}
                  {localizeIssueAction(i.gate, view) === 'ACCEPT_WORLD' && (
                    <Popconfirm
                      title="Duyệt bối cảnh này?" description="Sẽ ghi vào bối cảnh đích của truyện — mọi ảnh/kịch bản sau đó bám theo."
                      okText="Duyệt" cancelText="Thôi" onConfirm={() => acceptWorld()}
                    >
                      <Button size="small" type="primary" loading={busy === 'accept-world'} disabled={working}>Duyệt</Button>
                    </Popconfirm>
                  )}
                  {localizeIssueAction(i.gate, view) === 'OPTIONS' && optionsBtn({ kind: 'violationGate', gate: i.gate, message: i.message })}
                </Text>
              ))}
            </Space>
          )}
        />
      )}
      <OtherIssuesBlock rows={otherIssues} />
      {/* E9-P1: "Còn N tên chưa ánh xạ" là con số đo được, thay cho niềm tin "chắc AI bắt đủ rồi". */}
      {aliasScan.hasRun ? (
        aliasScan.unmapped.length > 0 && (
          <Alert
            type="warning" showIcon
            message={`Còn ${aliasScan.unmapped.length} tên chưa ánh xạ`}
            action={optionsBtn({ kind: 'alias', terms: aliasScan.unmapped.slice(0, 30).map((u) => u.term) })}
            description={(
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text style={{ fontSize: 13 }}>
                  Nguyên tác dùng những tên này mà chưa gán được vào nhân vật/bối cảnh nào — kịch bản có thể
                  còn sót chúng. Gán bằng cách thêm vào tên/bí danh của đúng thực thể ở khung xương.
                </Text>
                <Space wrap size={4}>
                  {aliasScan.unmapped.slice(0, 30).map((u) => <Tag key={u.term}>{u.term}{u.count ? ` ×${u.count}` : ''}</Tag>)}
                  {aliasScan.unmapped.length > 30 && (
                    <Text type="secondary" style={{ fontSize: 13 }}>… và {aliasScan.unmapped.length - 30} tên nữa</Text>
                  )}
                </Space>
              </Space>
            )}
          />
        )
      ) : (
        <Alert
          type="warning" showIcon
          message="Chưa quét bí danh từ nguyên tác"
          description="Bảng tên hiện chỉ có tên khung xương khai. Nếu truyện gọi nhân vật bằng cách khác (khung xương ghi “Coach” mà truyện viết “Coach Miller”) thì bước đổi tên sẽ sót. Bấm “Bồi chỗ trống” để quét."
        />
      )}
    </Space>
  ) : null;

  const glossaryBlock = hasRun ? (
    <Card
      size="small" data-tour="vocab" title={`Từ vựng (${view?.glossary?.length ?? 0})`}
      extra={summary.glossaryAuto > 0 && (
        <Popconfirm
          title={termsView.enabled ? acceptAllCopy(summary.glossaryAuto, termsView.counts.AUTO) : `Chấp nhận hết ${summary.glossaryAuto} term chờ?`}
          onConfirm={() => mutate('approve-all', () => adaptationApi.approveAllGlossary(storyId), 'Đã chấp nhận hết')}
        >
          <Button size="small" disabled={working}>Chấp nhận hết ({summary.glossaryAuto})</Button>
        </Popconfirm>
      )}
    >
      {termsView.enabled && (
        <Radio.Group
          size="small" optionType="button" buttonStyle="solid" value={termFilter} aria-label="Lọc từ vựng"
          style={{ marginBottom: 8 }} onChange={(e) => setTermFilter(e.target.value)}
          options={termFilterOptions(termsView.counts)}
        />
      )}
      <Table
        rowKey="id" size="small"
        pagination={termsView.rows.length > 12 ? { pageSize: 12, size: 'small' } : false}
        dataSource={termsView.rows.map((r) => r.term)} columns={glossaryColumns as any}
        locale={termsView.emptyText ? { emptyText: termsView.emptyText } : undefined}
      />
      <Space.Compact style={{ width: '100%', marginTop: 8 }}>
        <Input
          size="small" placeholder="Trong truyện" value={newTerm.o}
          onChange={(e) => setNewTerm((s) => ({ ...s, o: e.target.value }))}
        />
        <Input
          size="small" placeholder="Trong phim" value={newTerm.l}
          onChange={(e) => setNewTerm((s) => ({ ...s, l: e.target.value }))}
        />
        <Select
          size="small" style={{ width: 130 }} value={newTerm.t} options={GLOSSARY_TYPE_OPTIONS}
          onChange={(t) => setNewTerm((s) => ({ ...s, t }))}
        />
        <Button
          size="small" type="primary" disabled={working || !newTerm.o.trim() || !newTerm.l.trim()}
          onClick={() => directOps('add-term',
            [{ op: 'glossaryUpsert', originalTerm: newTerm.o.trim(), localizedTerm: newTerm.l.trim(), type: newTerm.t }],
            'Đã thêm term',
          ).then(() => setNewTerm({ o: '', l: '', t: 'OTHER' }))}
        >
          Thêm
        </Button>
      </Space.Compact>
    </Card>
  ) : null;

  const techItems = [
    ...(!gateOutside && gateInfo ? [{ key: 'gate', label: 'Cổng AI review', children: gateCard }] : []),
    ...(hasRun ? [{
      key: 'stats', label: 'Số liệu & thao tác',
      children: (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {summary.charactersNamed}/{summary.charactersTotal} nhân vật có tên · {summary.locked} đang giữ ·
            từ vựng {summary.glossaryApproved} đã chấp nhận / {summary.glossaryAuto} chờ
            {aliasScan.hasRun ? ` · bí danh ${aliasScan.attached} (quét ${aliasScan.chapters} chương)` : ''}
          </Text>
          <Popconfirm
            title="Trích lại quan hệ?"
            description="Chạy nền, tốn AI — AI đọc lại bản địa hoá để tìm quan hệ giữa các nhân vật."
            okText="Trích lại" cancelText="Thôi" disabled={working}
            onConfirm={() => runBg('rel', () => adaptationApi.runRelationships(storyId))}
          >
            <Button size="small" loading={busy === 'rel'} disabled={working}>Trích lại quan hệ</Button>
          </Popconfirm>
        </Space>
      ),
    }] : []),
    ...((versions?.length ?? 0) > 0 ? [{
      key: 'versions', label: `Phiên bản ③ (${versions!.length})`,
      children: (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Text type="secondary" style={{ fontSize: 13 }}>Mỗi lần sửa tay hoặc AI chạy lưu một bản. Khôi phục đổi lại tên / bối cảnh / từ vựng, thay tên NGƯỢC trên kịch bản đã viết và đẩy lại Chapters.</Text>
          {versions!.map((v) => (
            <Space key={v.id} size={4} wrap>
              <Text style={{ fontSize: 13 }}>{versionTitle(v, 'localize')}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>{new Date(v.createdAt).toLocaleString('vi-VN')}{v.createdBy ? ` · ${v.createdBy}` : ''}</Text>
              <Button size="small" type="link" onClick={() => openVersion(v)}>Xem</Button>
              <Popconfirm
                title={`Khôi phục v${v.versionNo}?`} okText="Khôi phục" cancelText="Thôi"
                description="Tên / bối cảnh / từ vựng về bản này; kịch bản đã viết bị thay tên ngược; chương Chapters đã đẩy được cập nhật (trừ chương có Film Plan / đã sửa tay)."
                onConfirm={() => restoreVersion(v)}
              >
                <Button size="small" type="link" danger loading={busy === `rv-${v.id}`} disabled={working}>Khôi phục</Button>
              </Popconfirm>
            </Space>
          ))}
        </Space>
      ),
    }] : []),
    ...((view?.factions?.length ?? 0) > 0 ? [{
      key: 'factions', label: 'Nhóm gia tộc / thế lực',
      children: (
        <Space wrap size={4}>
          {view!.factions.map((f) => {
            const members = f.memberIds.map((m) => view!.characters.find((c) => c.id === m)?.name ?? m).join(', ');
            return (
              <Tooltip key={f.id} title={members}>
                <Tag color={f.kind === 'family' ? 'blue' : 'default'}>{f.label} · {f.kind === 'family' ? 'gia tộc (cùng họ)' : 'tổ chức'}</Tag>
              </Tooltip>
            );
          })}
        </Space>
      ),
    }] : []),
    ...(view?.sourceWorldSetting ? [{
      key: 'src', label: 'Bối cảnh gốc của truyện',
      children: <Text style={{ fontSize: 13 }}>{worldLine(view.sourceWorldSetting)}</Text>,
    }] : []),
  ];

  return (
    <div id="section-localize">
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {gateOutside && gateCard}
        {hasRun && actionRow}
        {bgJobId && (
          <Alert
            type="info" showIcon
            message="Đang chạy bản địa hoá ở nền"
            description={`job ${bgJobId.slice(0, 8)}… — trích quan hệ + đặt tên + từ vựng. Nút bị khoá tới khi xong.`}
          />
        )}
        {policyBlock}
        {(hasRun || proposedWorld || !world.empty) && worldBlock}
        {!hasRun && (
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Alert
              type="warning" showIcon
              message="Chưa bản địa hoá"
              description={`AI sẽ trích quan hệ nhân vật, chọn bối cảnh theo cách “${cultureLabel}”, đặt tên cho mọi nhân vật/địa điểm (bố–con cùng họ) và dựng bảng từ vựng văn hoá.`}
            />
            <Space wrap>
              <Popconfirm
                title="Chạy bản địa hoá?" description="Việc này tốn AI; chỉ chạy khi bạn xác nhận."
                okText="Chạy" cancelText="Thôi" disabled={working}
                onConfirm={() => runBg('run', () => adaptationApi.runLocalize(storyId))}
              >
                <Button type="primary" cta={stageCta} loading={!!bgJobId || busy === 'run'} disabled={working}>
                  Chạy bản địa hoá
                </Button>
              </Popconfirm>
              <Button onClick={load} disabled={working}>Tải lại</Button>
            </Space>
          </Space>
        )}
        {hasRun && namesCard}
        {needsBlock}
        {glossaryBlock}
        <ConsequenceModal
          open={!!delTerm} onClose={() => setDelTerm(null)} danger
          title={delTerm ? glossaryDeleteCopy(delTerm.originalTerm, delTerm.localizedTerm).title : ''} lead={delTerm ? glossaryDeleteCopy(delTerm.originalTerm, delTerm.localizedTerm).lead : ''}
          okText="Xoá từ" errorFallback="Không xoá được từ"
          load={() => adaptationApi.glossaryDeleteImpact(storyId, delTerm!.id)}
          fallback={<p>{delTerm ? glossaryDeleteCopy(delTerm.originalTerm, delTerm.localizedTerm).fallback : ''}</p>}
          onConfirm={async (view) => {
            const t = delTerm;
            if (!t) return;
            const base = view?.base.outlineModifiedAt;
            await applyLocalizeOpsDirect(storyId, [{ op: 'glossaryDelete', originalTerm: t.originalTerm }], typeof base === 'string' ? base : undefined);
            await load();
            onChanged();
          }}
        />
        <LocalizeEditModal
          storyId={storyId} ops={pending?.ops ?? null} title={pending?.title ?? ''} screenModifiedAt={outlineModifiedAt ?? null}
          onClose={() => setPending(null)} onApplied={() => { load(); onChanged(); }} onStale={() => { load(); onChanged(); }}
        />
        <Modal
          open={!!worldEdit} title="Sửa bối cảnh phim" okText="Xem thử" cancelText="Thôi" onCancel={() => setWorldEdit(null)} destroyOnClose
          onOk={() => {
            if (!worldEdit) return;
            if (!worldEdit.era?.trim() && !worldEdit.world?.trim()) { message.warning('Cần ít nhất Thời đại hoặc Nơi chốn'); return; }
            const ws = worldEdit; setWorldEdit(null);
            setPending({ ops: [{ op: 'setWorldSetting', worldSetting: ws }], title: 'Đổi bối cảnh phim' });
          }}
        >
          {worldEdit && (
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              <Alert type="warning" showIcon message="Tập đã viết sẽ thành 'cần xem lại' — hệ thống không tự sửa chữ." />
              {(['era', 'world', 'architecture', 'costume'] as const).map((k) => (
                <Space key={k} direction="vertical" size={0} style={{ width: '100%' }}>
                  <Text type="secondary" style={{ fontSize: 13 }}>{({ era: 'Thời đại', world: 'Nơi chốn', architecture: 'Kiến trúc', costume: 'Trang phục' } as const)[k]}</Text>
                  <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} value={worldEdit[k] ?? ''} onChange={(e) => setWorldEdit((w) => (w ? { ...w, [k]: e.target.value } : w))} />
                </Space>
              ))}
            </Space>
          )}
        </Modal>
        <Modal open={!!viewVer} title={viewVer ? versionTitle(viewVer, 'localize') : ''} footer={null} onCancel={() => setViewVer(null)} destroyOnClose>
          {viewVer && (
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text style={{ fontSize: 13 }}><Text strong>Bối cảnh: </Text>{worldLine(viewVer.worldSetting) || '(chưa có)'}</Text>
              <Text strong style={{ fontSize: 13 }}>Nhân vật</Text>
              {(viewVer.outline?.characters ?? []).filter((c: any) => c.localizedName).map((c: any) => (
                <Text key={c.id} style={{ fontSize: 13 }}>{c.name} → {c.localizedName}{c.localizeMeta?.locked ? ' (giữ)' : ''}</Text>
              ))}
              <Text strong style={{ fontSize: 13 }}>Bối cảnh (địa điểm)</Text>
              {(viewVer.outline?.locations ?? []).filter((l: any) => l.localizedName).map((l: any) => (
                <Text key={l.id} style={{ fontSize: 13 }}>{l.name} → {l.localizedName}</Text>
              ))}
              <Text style={{ fontSize: 13 }}><Text strong>Từ vựng: </Text>{viewVer.glossary?.length ?? 0} dòng</Text>
            </Space>
          )}
        </Modal>
        <Modal open={!!restoreResult} title="Kết quả khôi phục" footer={<Button type="primary" onClick={() => setRestoreResult(null)}>Đóng</Button>} onCancel={() => setRestoreResult(null)} destroyOnClose>
          {restoreResult && renderSections(restoreResult.sections.length ? restoreResult.sections : [{ key: 'ok', tone: 'success', title: restoreResult.success, lines: [] }])}
        </Modal>
        {techItems.length > 0 && (
          <Collapse size="small" ghost items={[{
            key: 'tech', label: <Text type="secondary" style={{ fontSize: 12 }}>Chi tiết kỹ thuật</Text>,
            children: <Collapse size="small" items={techItems} />,
          }]} />
        )}
      </Space>
    </div>
  );
};

export default AdaptationLocalize;
